import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  AlertCircle,
  ArrowUp,
  Check,
  History,
  Loader2,
  SquarePen,
  Square,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useClassificationTargets, useInboxes, useSubscription } from '@/api'
import { request, type SelectionPage, type Snapshot } from '@/api/setup-chat'
import { useSetupChat } from '@/hooks/use-setup-chat'
import { PlanCard } from '@/components/setup/plan-card'
import { RunResults } from '@/components/setup/run-results'

type Approval = Snapshot['approvals'][number]

const SUGGESTIONS = [
  'Help me organise my invoices',
  'Archive newsletters I never read',
  'What does my setup do right now?',
]

function changeText(value: string | null): string {
  if (value == null) return 'None'
  let parsed: unknown
  try {
    parsed = JSON.parse(value)
  } catch {
    return value.trimStart().startsWith('{') ||
      value.trimStart().startsWith('[')
      ? 'Review settings'
      : value
  }
  if (typeof parsed === 'string') return parsed
  if (Array.isArray(parsed)) {
    const titles = parsed
      .map((item) => (typeof item?.title === 'string' ? item.title : ''))
      .filter(Boolean)
    return titles.length ? titles.join(' → ') : 'Review settings'
  }
  if (!parsed || typeof parsed !== 'object') return 'Review settings'
  const data = parsed as Record<string, unknown>
  if (typeof data.title === 'string' && typeof data.content === 'string') {
    return `${data.title}: ${data.content}${data.active === false ? ' (paused)' : ''}`
  }
  if (typeof data.name === 'string') return data.name
  return 'Review settings'
}

type Settings = {
  permissions: Record<string, unknown>
  assessments: Record<string, unknown>
}
function settingsOf(value: string | null): Settings | null {
  if (value == null) return null
  try {
    const data = JSON.parse(value) as Record<string, unknown> | null
    return data &&
      typeof data.permissions === 'object' &&
      data.permissions &&
      typeof data.assessments === 'object' &&
      data.assessments
      ? (data as Settings)
      : null
  } catch {
    return null
  }
}
const FLAGS: [group: keyof Settings, key: string, on: string, off: string][] = [
  ['permissions', 'mark_read', 'Can now mark emails read', 'Can no longer mark emails read'],
  ['permissions', 'archive', 'Can now archive emails', 'Can no longer archive emails'],
  ['permissions', 'move', 'Can now move emails', 'Can no longer move emails'],
  ['permissions', 'labels', 'Can now add labels', 'Can no longer add labels'],
  ['permissions', 'categories', 'Can now set categories', 'Can no longer set categories'],
  ['assessments', 'spam', 'Now checks for spam', 'No longer checks for spam'],
  ['assessments', 'phishing', 'Now checks for phishing', 'No longer checks for phishing'],
]
const TARGETS: [key: string, verb: string, one: string, many: string][] = [
  ['allowed_label_ids', 'label', 'label', 'labels'],
  ['allowed_move_target_ids', 'move to', 'folder', 'folders'],
  ['allowed_category_ids', 'categorise as', 'category', 'categories'],
]
// Mailbox settings changes as plain sentences, e.g. "Can now label: Orders".
function settingsSentences(
  before: Settings | null,
  after: Settings,
  name: (id: string) => string | undefined,
): string[] {
  const ids = (settings: Settings | null, key: string) => {
    const value = settings?.permissions[key]
    return Array.isArray(value) ? value.map(String) : []
  }
  // Names come from the inbox's current labels and folders; any not found
  // (deleted, or still loading) are counted instead.
  const list = (values: string[], one: string, many: string) => {
    const named = values.map(name).filter((v): v is string => !!v)
    const rest = values.length - named.length
    if (rest)
      named.push(named.length ? `${rest} more` : `${rest} ${rest === 1 ? one : many}`)
    return named.join(', ')
  }
  const lines: string[] = []
  for (const [group, key, on, off] of FLAGS) {
    const was = !!before?.[group][key]
    const now = !!after[group][key]
    if (was !== now) lines.push(now ? on : off)
  }
  for (const [key, verb, one, many] of TARGETS) {
    const was = ids(before, key)
    const now = ids(after, key)
    const added = now.filter((id) => !was.includes(id))
    const removed = was.filter((id) => !now.includes(id))
    if (added.length) lines.push(`Can now ${verb}: ${list(added, one, many)}`)
    if (removed.length) lines.push(`Can no longer ${verb}: ${list(removed, one, many)}`)
  }
  return lines.length ? lines : ['No changes']
}
function SettingsChange({
  inboxId,
  before,
  after,
}: {
  inboxId: string
  before: Settings | null
  after: Settings
}) {
  const targets = useClassificationTargets(inboxId)
  const names = new Map(
    [
      ...(targets.data?.labels ?? []),
      ...(targets.data?.folders ?? []),
      ...(targets.data?.categories ?? []),
    ].map((target) => [target.id, target.name]),
  )
  return (
    <ul className="space-y-1">
      {settingsSentences(before, after, (id) => names.get(id)).map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  )
}

function recordedAction(
  action: Snapshot['simulations'][number]['recorded_actions'][number],
) {
  const names = {
    mark_read: 'Mark as read',
    archive: 'Archive',
    folder: 'Move',
    label: 'Label',
    category: 'Category',
  }
  const statuses = {
    succeeded: 'completed',
    skipped: 'skipped',
    unknown: 'unconfirmed',
    failed: 'failed',
    cancelled: 'stopped',
    started: 'in progress',
    pending: 'waiting',
  }
  return `${names[action.kind]}${action.target_name ? ` · ${action.target_name}` : ''} · ${statuses[action.status]}`
}

// Assistant replies use light markdown: paragraphs, bullet or numbered lists,
// headings and **bold**. Rendered as React elements, never as HTML.
function inline(text: string) {
  // While a reply streams, a bold marker can arrive before its pair: don't show it raw.
  if ((text.split('**').length - 1) % 2) {
    const open = text.lastIndexOf('**')
    text = text.slice(0, open) + text.slice(open + 2)
  }
  return text
    .split(/(\*\*[^*]+\*\*)/g)
    .map((part, i) =>
      part.startsWith('**') && part.endsWith('**') && part.length > 4 ? (
        <strong key={i} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      ) : (
        <Fragment key={i}>
          {part.split(/(Inboxes page)/g).map((piece, j) =>
            piece === 'Inboxes page' ? (
              <Link
                key={j}
                to="/inboxes"
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                {piece}
              </Link>
            ) : (
              piece
            ),
          )}
        </Fragment>
      ),
    )
}
function RichText({ text }: { text: string }) {
  const blocks: ReactNode[] = []
  let paragraph: string[] = []
  let list: { ordered: boolean; items: string[] } | null = null
  const flush = () => {
    if (paragraph.length)
      blocks.push(
        <p key={blocks.length} className="whitespace-pre-line">
          {inline(paragraph.join('\n'))}
        </p>,
      )
    if (list) {
      const Tag = list.ordered ? 'ol' : 'ul'
      blocks.push(
        <Tag
          key={blocks.length}
          className={`space-y-1 pl-5 ${list.ordered ? 'list-decimal' : 'list-disc'} marker:text-muted-foreground`}
        >
          {list.items.map((item, i) => (
            <li key={i} className="pl-1">
              {inline(item)}
            </li>
          ))}
        </Tag>,
      )
    }
    paragraph = []
    list = null
  }
  for (const line of text.split('\n')) {
    const bullet = /^\s*(?:[-*•]|(\d+)[.)])\s+(.*)$/.exec(line)
    const heading = /^\s*#{1,6}\s+(.*)$/.exec(line)
    if (bullet) {
      const ordered = bullet[1] !== undefined
      if (paragraph.length || (list && list.ordered !== ordered)) flush()
      list ??= { ordered, items: [] }
      list.items.push(bullet[2] ?? '')
    } else if (heading) {
      flush()
      blocks.push(
        <p key={blocks.length} className="font-semibold">
          {inline(heading[1] ?? '')}
        </p>,
      )
    } else if (!line.trim()) {
      flush()
    } else {
      if (list) flush()
      paragraph.push(line)
    }
  }
  flush()
  return <div className="space-y-3">{blocks}</div>
}

function Selection({
  session,
  approval,
  token,
  inboxName,
}: {
  session: string
  approval: Approval
  token: () => Promise<string>
  inboxName: (id: string) => string | undefined
}) {
  const [items, setItems] = useState(approval.selection?.items ?? [])
  const [cursor, setCursor] = useState(approval.selection?.next_cursor ?? null)
  const [open, setOpen] = useState(false)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)
  async function more() {
    if (!cursor || loading) return
    setLoading(true)
    try {
      const page = await request<SelectionPage>(
        await token(),
        `/sessions/${session}/approvals/${approval.id}/selection?cursor=${encodeURIComponent(cursor)}&limit=50`,
      )
      setItems((previous) => [...previous, ...page.items])
      setCursor(page.next_cursor)
      setError(false)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }
  if (!approval.selection) return null
  return (
    <div className="space-y-2 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p>
          {approval.selection.count}{' '}
          {approval.selection.count === 1 ? 'email' : 'emails'} ·{' '}
          {new Date(approval.selection.window_start).toLocaleDateString()}–
          {new Date(approval.selection.window_end).toLocaleDateString()}
        </p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="-mr-2 text-primary"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {open ? 'Hide emails' : 'Review emails'}
        </Button>
      </div>
      {open && (
        <div className="space-y-2">
          <ul className="max-h-64 divide-y overflow-y-auto rounded-lg bg-muted/50 px-3">
            {items.map((item) => (
              <li key={`${item.inbox_id}-${item.id}`} className="py-2">
                <span className="block font-medium">
                  {item.subject || '(No subject)'}
                </span>
                <span className="block text-muted-foreground">
                  {item.sender || 'Unknown sender'} ·{' '}
                  {new Date(item.received_at).toLocaleDateString()}
                  {inboxName(item.inbox_id)
                    ? ` · ${inboxName(item.inbox_id)}`
                    : ''}
                </span>
              </li>
            ))}
          </ul>
          {cursor && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={more}
              disabled={loading}
            >
              {loading ? 'Loading…' : 'Load more'}
            </Button>
          )}
          {error && <p role="alert">Could not load emails. Try again.</p>}
        </div>
      )}
    </div>
  )
}

function chatLabel(title: string, updated: string) {
  return title && title !== 'New setup' && title !== 'Setup'
    ? title
    : `Chat from ${new Date(updated).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
}

// What the chat is doing, from the tool it last started. Never its reasoning.
const STEPS: Record<string, string> = {
  list_inboxes: 'Checking your inboxes',
  get_setup: 'Reading your rules',
  list_resources: 'Checking labels and folders',
  list_recent_mail: 'Looking through your recent email',
  search_recent_mail: 'Searching your recent email',
  overview_recent_mail: 'Looking through your recent email',
  read_recent_mail: 'Reading an email',
  read_recent_mail_batch: 'Reading a few emails',
  get_processing_history: 'Checking what was sorted before',
  create_instruction: 'Saving the rule',
  update_instruction: 'Updating the rule',
  delete_instruction: 'Removing the rule',
  reorder_instructions: 'Reordering your rules',
  set_permissions: 'Updating inbox settings',
  create_resource: 'Creating the label',
  propose_resource_change: 'Preparing the change',
  ask_organization_question: 'Writing a question',
  simulate_email: 'Testing a rule on an email',
  propose_historical_run: 'Preparing the run',
  get_run_results: 'Checking the run',
  propose_categorize_plan: 'Writing the plan',
}
function stepWords(run: Snapshot['runs'][number], next: boolean) {
  if (run.cancel_requested)
    return next ? 'Wrapping up to answer your message…' : 'Stopping…'
  if (run.status === 'waiting_children')
    return next
      ? 'Researching your inboxes; your message is next'
      : 'Researching your inboxes'
  if (run.step === 'research_inboxes') return 'Going through what it found'
  return `${(run.step && STEPS[run.step]) || 'Reading your message'}…`
}

// New arrivals settle in from just below; what was already there stays still.
const ENTER =
  'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-300 motion-safe:ease-out'

// `mention` is an inbox to name in the message box, from "Ask about this inbox".
export function SetupConversation({ mention }: { mention?: string }) {
  const chat = useSetupChat()
  const navigate = useNavigate()
  const inboxes = useInboxes()
  const subscription = useSubscription()
  const [text, setText] = useState('')
  const [confirmReset, setConfirmReset] = useState(false)
  const [now, setNow] = useState(Date.now)
  const composer = useRef<HTMLTextAreaElement>(null)
  const end = useRef<HTMLDivElement>(null)
  const atEnd = useRef(true)
  const snapshot = chat.snapshot
  // Only what arrives after a chat opens animates in, never the history.
  const [opened, setOpened] = useState<{ id: string; seq: number } | null>(null)
  if (snapshot && opened?.id !== snapshot.id)
    setOpened({ id: snapshot.id, seq: snapshot.last_event_seq })
  const arrived = (seq: number) => (opened && seq > opened.seq ? ENTER : '')
  // Past-mail runs get one progress-then-results card; RunResults shows nothing for
  // a run that never queued an email, and the failed progress row explains it.
  const runCards = new Set(
    (snapshot?.approvals ?? [])
      .filter(
        (a) =>
          (a.kind === 'historical_run' || a.kind === 'categorize_plan') &&
          (a.status === 'applying' ||
            a.status === 'completed' ||
            a.status === 'unknown' ||
            a.status === 'failed'),
      )
      .map((a) => a.id),
  )
  const hasPendingApproval = !!snapshot?.approvals.some(
    (a) => a.status === 'pending',
  )
  useEffect(() => {
    if (!hasPendingApproval) return
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [hasPendingApproval])
  // Follow new content only while the reader is already at the end.
  useEffect(() => {
    const node = end.current
    if (!node) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        atEnd.current = !!entry?.isIntersecting
      },
      { rootMargin: '0px 0px 160px 0px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])
  const sessionId = snapshot?.id
  useLayoutEffect(() => {
    atEnd.current = true
  }, [sessionId])
  useLayoutEffect(() => {
    if (atEnd.current) end.current?.scrollIntoView({ block: 'end' })
  }, [snapshot])
  useLayoutEffect(() => {
    const node = composer.current
    if (!node) return
    node.style.height = 'auto'
    node.style.height = `${Math.min(node.scrollHeight, 160)}px`
  }, [text])

  const name = (id: string | null | undefined) =>
    inboxes.data?.find((i) => i.uuid === id)?.display_name
  // "Ask about this inbox" starts the message with the inbox's name, once.
  const [mentioned, setMentioned] = useState<string>()
  const mentionName = name(mention)
  if (mention && mention !== mentioned && mentionName) {
    setMentioned(mention)
    setText(`About ${mentionName}: `)
  }
  useEffect(() => {
    if (!mentioned) return
    const node = composer.current
    node?.focus()
    node?.setSelectionRange(node.value.length, node.value.length)
    void navigate({ to: '/setup', search: {}, replace: true })
  }, [mentioned, navigate])
  const question = snapshot?.questions.find((q) => q.status === 'open')
  const run = snapshot?.runs.find((r) => r.role === 'parent')
  // A message sent while the chat was working, waiting to be answered next.
  const waitingTurn = !!snapshot?.runs.some((r) => r.status === 'waiting_turn')
  const outgoing = chat.outgoing
  const empty = (!snapshot || snapshot.timeline.length === 0) && !outgoing
  // One sign of progress at a time: the reply itself once it is streaming.
  const streaming = !!snapshot?.messages.some((m) => m.status === 'streaming')
  const automation =
    subscription.data?.subscription?.status === 'PAST_DUE'
      ? 'payment'
      : subscription.data && !subscription.data.has_subscription
        ? 'off'
        : inboxes.data?.length && !inboxes.data.some((i) => i.is_active)
          ? 'off'
          : null

  const workRow = (work: Snapshot['work'][number]) => (
    <div
      key={work.id}
      className="flex items-start gap-2.5 rounded-lg bg-muted/60 px-3 py-2 text-sm"
    >
      {work.status === 'running' || work.status === 'queued' ? (
        <Loader2
          aria-hidden
          className="mt-0.5 size-4 shrink-0 text-primary motion-safe:animate-spin"
        />
      ) : work.status === 'completed' ? (
        <Check
          aria-hidden
          className="mt-0.5 size-4 shrink-0 text-green-600 dark:text-green-500"
        />
      ) : (
        <AlertCircle
          aria-hidden
          className="mt-0.5 size-4 shrink-0 text-muted-foreground"
        />
      )}
      <div className="min-w-0">
        {work.inbox_id && name(work.inbox_id) ? (
          // Inbox research: the inbox, then what it is doing right now.
          <>
            <p className="font-medium">{name(work.inbox_id)}</p>
            <p className="text-muted-foreground tabular-nums">
              {work.status === 'running'
                ? `${work.label === 'Inbox research' ? 'Starting' : work.label}${
                    work.completed != null
                      ? ` · ${work.completed.toLocaleString()} ${work.completed === 1 ? 'email' : 'emails'} so far`
                      : ''
                  }`
                : {
                    queued: 'Waiting to start',
                    running: 'Working',
                    completed: 'Done',
                    partial: 'Partly done',
                    failed: 'Could not finish',
                    cancelled: 'Stopped',
                  }[work.status]}
            </p>
          </>
        ) : (
          <>
            <p>
              <span className="font-medium">{work.label}</span>
              {name(work.inbox_id) ? ` · ${name(work.inbox_id)}` : ''}
            </p>
            <p className="text-muted-foreground">
              {
                {
                  queued: 'Waiting',
                  running: 'Working',
                  completed: 'Done',
                  partial: 'Partially complete',
                  failed: 'Could not finish',
                  cancelled: 'Stopped',
                }[work.status]
              }
              {work.completed != null && work.total != null
                ? ` · ${work.completed} of ${work.total}`
                : ''}
            </p>
          </>
        )}
        {work.error && <p>{work.error.message}</p>}
      </div>
    </div>
  )
  // The run keeps its waiting status until it resumes; once the approval is
  // decided (or expired) there is nothing left for the person to do.
  const awaitingDecision =
    run?.status === 'waiting_approval' &&
    !!snapshot?.approvals.some(
      (a) => a.status === 'pending' && Date.parse(a.expires_at) > now,
    )
  const submit = async (value: string) => {
    // The message moves into the chat at once; it comes back here only if it failed.
    setText('')
    if (!(await chat.send(value, question?.id))) setText(value)
  }
  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    void submit(text)
  }
  const reset = () => {
    if (run || hasPendingApproval) setConfirmReset(true)
    else void chat.newSession().then(() => composer.current?.focus())
  }

  return (
    <div className="mx-auto -my-4 flex min-h-[calc(100dvh-4rem)] w-full min-w-0 max-w-3xl flex-col lg:-my-6">
      <header className="sticky top-0 z-10 -mx-4 flex items-center gap-2 border-b bg-background px-4 py-2.5 sm:mx-0 sm:px-0">
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold leading-tight">Setup</h1>
        </div>
        {chat.sessions.length > 1 && (
          <DropdownMenu
            onOpenChange={(open) => {
              if (open) void chat.refreshSessions().catch(() => {})
            }}
          >
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-10 shrink-0"
                aria-label="Past chats"
                disabled={chat.loading}
              >
                <History className="size-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="max-h-[min(24rem,var(--radix-dropdown-menu-content-available-height))] w-72 max-w-[calc(100vw-2rem)]"
            >
              {chat.sessions.map((session) => {
                const current = session.id === snapshot?.id
                return (
                  <DropdownMenuItem
                    key={session.id}
                    disabled={chat.busy}
                    onSelect={() => {
                      if (!current) void chat.chooseSession(session.id)
                    }}
                    className="items-start gap-2 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">
                        {chatLabel(session.title, session.updated_at)}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {new Date(session.updated_at).toLocaleDateString(
                          undefined,
                          { month: 'short', day: 'numeric' },
                        )}
                      </p>
                    </div>
                    {current && (
                      <Check
                        aria-label="Current chat"
                        className="mt-0.5 size-4 text-primary"
                      />
                    )}
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {snapshot && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-10 shrink-0 gap-1.5 px-3"
            disabled={chat.busy || chat.loading || empty}
            onClick={reset}
          >
            <SquarePen className="size-4" />
            New chat
          </Button>
        )}
      </header>

      <Dialog open={confirmReset} onOpenChange={setConfirmReset}>
        <DialogContent showCloseButton={false} className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Start a new chat?</DialogTitle>
            <DialogDescription>
              {run
                ? 'This stops what’s running. Changes already made stay.'
                : 'Open requests in this chat will expire. Changes already made stay.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="grid grid-cols-2 gap-2 sm:grid sm:space-x-0">
            <Button variant="outline" onClick={() => setConfirmReset(false)}>
              Cancel
            </Button>
            <Button
              disabled={chat.busy}
              onClick={() => {
                setConfirmReset(false)
                void chat.newSession().then(() => composer.current?.focus())
              }}
            >
              New chat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="min-w-0 flex-1 space-y-6 py-6 text-[15px] [overflow-wrap:anywhere] sentry-block ph-no-capture">
        {chat.loading && (
          <p
            role="status"
            className="flex items-center gap-2 text-sm text-muted-foreground"
          >
            <Loader2 className="size-4 motion-safe:animate-spin" />
            Loading your conversation…
          </p>
        )}
        {!chat.loading && empty && !chat.error && (
          <div className="flex min-h-[40dvh] flex-col justify-end space-y-5 pb-2">
            <h2 className="text-2xl font-semibold tracking-tight text-balance">
              How should your email be organised?
            </h2>
            <div className="flex flex-col items-start gap-2">
              {SUGGESTIONS.map((suggestion) => (
                <Button
                  key={suggestion}
                  type="button"
                  variant="outline"
                  className="h-auto min-h-10 max-w-full whitespace-normal rounded-full px-4 py-2 text-left font-normal"
                  disabled={chat.busy || chat.uncertain}
                  onClick={() => void submit(suggestion)}
                >
                  {suggestion}
                </Button>
              ))}
            </div>
          </div>
        )}
        {snapshot?.timeline.map((entry) => (
          <div
            key={`${entry.kind}-${entry.id}`}
            className={`space-y-6 empty:hidden ${
              // The message you just sent was already on screen; don't replay it.
              entry.kind === 'message' &&
              snapshot.messages.some(
                (m) => m.id === entry.id && m.client_request_id === chat.lastSentId,
              )
                ? ''
                : arrived(entry.seq)
            }`}
          >
            {entry.kind === 'run_outcome' &&
              snapshot.run_outcomes
                .filter((outcome) => outcome.id === entry.id)
                .map((outcome) => (
                  <p
                    key={outcome.id}
                    role="status"
                    className="text-center text-sm text-muted-foreground"
                  >
                    {outcome.status === 'cancelled'
                      ? 'Stopped'
                      : (outcome.error &&
                          [
                            'setup_budget_turn',
                            'setup_budget_research',
                            'setup_budget_daily',
                          ].includes(outcome.error.code) &&
                          outcome.error.message) ||
                        'Could not finish. Review the conversation before continuing.'}
                  </p>
                ))}
            {entry.kind === 'message' &&
              snapshot.messages
              .filter((message) => message.id === entry.id)
              .map((message) =>
                message.role === 'user' ? (
                  <div key={message.id} className="flex justify-end">
                    <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-primary px-4 py-2.5 text-primary-foreground sm:max-w-[75%]">
                      {message.text}
                    </div>
                  </div>
                ) : (
                  <div key={message.id} className="leading-relaxed">
                    <RichText text={message.text} />
                    {message.status === 'interrupted' && (
                      <p className="mt-2 text-xs text-muted-foreground">
                        Interrupted
                      </p>
                    )}
                  </div>
                ),
              )}
            {entry.kind === 'work' &&
              snapshot.work
                .filter(
                  (work) =>
                    work.id === entry.id &&
                    // A run's progress is shown under its approval card.
                    !snapshot.approvals.some((a) => a.id === work.id),
                )
                .map(workRow)}
            {entry.kind === 'change' &&
              snapshot.changes
              .filter((change) => change.id === entry.id)
              .map((change) => (
                <div
                  key={change.id}
                  className="space-y-2 rounded-xl border bg-card p-4 text-sm"
                >
                  <div className="flex items-start gap-2">
                    {change.status === 'saved' ? (
                      <Check
                        aria-hidden
                        className="mt-0.5 size-4 shrink-0 text-green-600 dark:text-green-500"
                      />
                    ) : (
                      <AlertCircle
                        aria-hidden
                        className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                      />
                    )}
                    <h3 className="font-semibold">
                      {change.status === 'saved' ? 'Saved' : 'Needs review'} ·{' '}
                      {change.title}
                      {name(change.inbox_id)
                        ? ` · ${name(change.inbox_id)}`
                        : ''}
                    </h3>
                  </div>
                  <div className="space-y-1 pl-6">
                    {change.inbox_id && settingsOf(change.after) ? (
                      <SettingsChange
                        inboxId={change.inbox_id}
                        before={settingsOf(change.before)}
                        after={settingsOf(change.after)!}
                      />
                    ) : (
                      <>
                        {change.before != null && (
                          <p className="text-muted-foreground line-through decoration-muted-foreground/50">
                            {changeText(change.before)}
                          </p>
                        )}
                        <p>{changeText(change.after)}</p>
                      </>
                    )}
                    <Link
                      to={
                        change.review_path?.startsWith('/settings') ||
                        change.scope === 'USER'
                          ? '/settings'
                          : '/inboxes'
                      }
                      className="inline-block pt-1 font-medium text-primary underline-offset-4 hover:underline"
                    >
                      Review settings
                    </Link>
                  </div>
                </div>
              ))}
            {entry.kind === 'simulation' &&
              snapshot.simulations
              .filter((test) => test.id === entry.id)
              .map((test) => (
                <div
                  key={test.id}
                  className="space-y-2 rounded-xl border bg-card p-4 text-sm"
                >
                  <h3 className="font-semibold">
                    Test ·{' '}
                    {test.source === 'synthetic'
                      ? 'Example email'
                      : 'Recent email'}
                    {name(test.inbox_id) ? ` · ${name(test.inbox_id)}` : ''}
                  </h3>
                  {(test.subject || test.sender) && (
                    <div>
                      {test.subject && (
                        <p className="font-medium">{test.subject}</p>
                      )}
                      {test.sender && (
                        <p className="text-muted-foreground">{test.sender}</p>
                      )}
                    </div>
                  )}
                  <p className="text-muted-foreground">
                    {test.setup_source === 'proposed'
                      ? 'Proposed setup'
                      : 'Saved setup'}
                    {test.received_at
                      ? ` · ${new Date(test.received_at).toLocaleDateString()}`
                      : ''}
                  </p>
                  <p>{test.summary}</p>
                  {test.effective_actions.length > 0 && (
                    <ul className="list-disc space-y-0.5 pl-5">
                      {test.effective_actions.map((action) => (
                        <li key={action}>{action}</li>
                      ))}
                    </ul>
                  )}
                  {test.blocked_actions.map((action) => (
                    <p key={action} className="text-muted-foreground">
                      Unavailable: {action}
                    </p>
                  ))}
                  {test.recorded_actions.length > 0 && (
                    <div>
                      <p className="font-medium">Earlier recorded actions</p>
                      {test.recorded_actions.map((action, index) => (
                        <p key={index}>{recordedAction(action)}</p>
                      ))}
                    </div>
                  )}
                  {test.prior_state !== 'known' && (
                    <p className="text-muted-foreground">
                      Earlier state{' '}
                      {test.prior_state === 'partial'
                        ? 'partly known'
                        : 'unknown'}
                    </p>
                  )}
                </div>
              ))}
            {entry.kind === 'approval' &&
              snapshot.approvals
              .filter((approval) => approval.id === entry.id)
              .map((approval) => {
                const open =
                  approval.status === 'pending' &&
                  Date.parse(approval.expires_at) > now
                return (
                  <Fragment key={approval.id}>
                    {approval.kind === 'categorize_plan' ? (
                      <PlanCard
                        approval={approval}
                        now={now}
                        disabled={chat.busy || chat.uncertain}
                        onDecide={(decision) =>
                          void chat.decide(
                            approval.id,
                            approval.revision,
                            decision,
                          )
                        }
                      />
                    ) : (
                    <div
                      className={`space-y-3 rounded-xl border bg-card p-4 ${open ? 'border-primary/50 shadow-sm' : ''}`}
                    >
                      <div>
                        <h3 className="font-semibold">
                          {approval.kind === 'historical_run'
                            ? 'Process older emails'
                            : 'Review change'}
                        </h3>
                        {name(approval.inbox_id) && (
                          <p className="text-sm text-muted-foreground">
                            {name(approval.inbox_id)}
                          </p>
                        )}
                      </div>
                      <p className="text-sm">{approval.summary}</p>
                      {approval.consequences.length > 0 && (
                        <ul className="list-disc space-y-0.5 pl-5 text-sm">
                          {approval.consequences.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      )}
                      {approval.selection && (
                        <Selection
                          session={snapshot.id}
                          approval={approval}
                          token={chat.getAccessToken}
                          inboxName={name}
                        />
                      )}
                      {approval.kind === 'historical_run' && (
                        <p className="text-sm font-medium">
                          This will change real emails.
                        </p>
                      )}
                      {open ? (
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            variant="outline"
                            className="h-11"
                            disabled={chat.busy || chat.uncertain}
                            onClick={() =>
                              void chat.decide(
                                approval.id,
                                approval.revision,
                                'deny',
                              )
                            }
                          >
                            Deny
                          </Button>
                          <Button
                            className="h-11"
                            disabled={chat.busy || chat.uncertain}
                            onClick={() =>
                              void chat.decide(
                                approval.id,
                                approval.revision,
                                'approve',
                              )
                            }
                          >
                            Approve
                          </Button>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {approval.status === 'pending'
                            ? 'Expired — ask for an updated review.'
                            : {
                                approved: 'Approved · Waiting to start',
                                denied: 'Denied',
                                expired: 'Expired — ask for an updated review.',
                                applying: 'Applying…',
                                completed: 'Completed',
                                stale: 'Changed — ask for an updated review.',
                                unknown:
                                  'Outcome unknown — review before continuing.',
                                failed:
                                  'Could not finish. Review before continuing.',
                              }[approval.status]}
                        </p>
                      )}
                    </div>
                    )}
                    {runCards.has(approval.id) && (
                      <RunResults
                        runId={approval.id}
                        fallback={snapshot.work
                          .filter(
                            (work) =>
                              work.id === approval.id && work.status !== 'failed',
                          )
                          .map(workRow)}
                      />
                    )}
                    {snapshot.work
                      .filter(
                        (work) =>
                          work.id === approval.id &&
                          // Past-mail runs show progress in RunResults; failures stay here.
                          (work.status === 'failed' || !runCards.has(work.id)),
                      )
                      .map(workRow)}
                  </Fragment>
                )
              })}
            {question &&
              entry.kind === 'question' &&
              question.id === entry.id && (
                <div className="space-y-3">
                  <p className="font-medium">{question.question}</p>
                  <div className="flex flex-wrap gap-2">
                    {question.options.map((option) => (
                      <Button
                        key={option.value}
                        variant="outline"
                        className="h-auto min-h-10 max-w-full whitespace-normal rounded-full px-4 py-2 text-left font-normal"
                        disabled={chat.busy || chat.uncertain}
                        onClick={() => void submit(option.label)}
                      >
                        {option.label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
          </div>
        ))}
        {outgoing && (
          <div className={`flex justify-end ${ENTER}`}>
            <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-primary px-4 py-2.5 text-primary-foreground sm:max-w-[75%]">
              {outgoing.text}
            </div>
          </div>
        )}
        {(run || outgoing) && !streaming && (
          <p
            role="status"
            className={`flex items-center gap-2 text-sm text-muted-foreground ${ENTER}`}
          >
            {!awaitingDecision && (
              <span className="flex gap-1" aria-hidden>
                {[0, 150, 300].map((delay) => (
                  <span
                    key={delay}
                    className="size-1.5 rounded-full bg-primary motion-safe:animate-pulse"
                    style={{ animationDelay: `${delay}ms` }}
                  />
                ))}
              </span>
            )}
            {awaitingDecision
              ? 'Waiting for your decision'
              : run
                ? stepWords(run, waitingTurn)
                : 'Reading your message…'}
          </p>
        )}
        {chat.connection === 'lost' && (
          <p role="status" className="text-sm text-muted-foreground">
            Connection lost. Reconnecting…
          </p>
        )}
        {chat.error && (
          <div
            role="alert"
            className="space-y-2 rounded-lg bg-destructive/10 px-3 py-2.5 text-sm text-destructive"
          >
            <p>{chat.error}</p>
            {chat.uncertain ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={chat.busy}
                  onClick={() => void chat.reconcile()}
                >
                  Check again
                </Button>
                {chat.canRetryCreate && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={chat.busy}
                    onClick={() => void chat.retryCreate()}
                  >
                    Try starting chat again
                  </Button>
                )}
              </div>
            ) : (
              !snapshot && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.location.reload()}
                >
                  Try again
                </Button>
              )
            )}
          </div>
        )}
        <div ref={end} className="scroll-mb-28" />
      </div>

      <form
        onSubmit={onSubmit}
        className="sticky bottom-0 -mx-4 bg-background px-4 pb-[max(env(safe-area-inset-bottom),1rem)] pt-2 sm:mx-0 sm:px-0"
      >
        {automation && (
          <p className="mb-2 flex flex-wrap items-center justify-between gap-x-3 px-1 text-sm text-muted-foreground">
            {automation === 'payment'
              ? 'Automatic sorting is paused until payment is updated.'
              : 'Automatic sorting is off.'}
            {automation === 'payment' ? (
              <Link
                to="/settings"
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                Update payment
              </Link>
            ) : (
              <Link
                to="/onboarding"
                search={{ manual: 'billing' }}
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                Turn on
              </Link>
            )}
          </p>
        )}
        <div className="flex items-end gap-2 rounded-3xl border bg-card p-1.5 pl-4 shadow-sm transition-shadow focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/30">
          <label htmlFor="setup-message" className="sr-only">
            Message setup
          </label>
          <textarea
            ref={composer}
            id="setup-message"
            rows={1}
            value={text}
            enterKeyHint="send"
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (
                event.key === 'Enter' &&
                !event.shiftKey &&
                !event.nativeEvent.isComposing
              ) {
                event.preventDefault()
                void submit(text)
              }
            }}
            placeholder="Message Setup…"
            className="max-h-40 min-h-10 min-w-0 flex-1 resize-none bg-transparent py-2.5 text-base leading-5 outline-none placeholder:text-muted-foreground"
          />
          {run && !text.trim() ? (
            <Button
              type="button"
              size="icon"
              variant="outline"
              className="size-10 shrink-0 rounded-full"
              aria-label="Stop"
              disabled={chat.busy || chat.uncertain || run.cancel_requested}
              onClick={() => void chat.stop()}
            >
              <Square className="size-3.5 fill-current" />
            </Button>
          ) : (
            <Button
              size="icon"
              className="size-10 shrink-0 rounded-full"
              aria-label="Send message"
              disabled={
                !text.trim() || chat.busy || chat.uncertain || chat.loading
              }
            >
              <ArrowUp className="size-5" />
            </Button>
          )}
        </div>
      </form>
    </div>
  )
}
